import { doc, getDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "@/lib/firebase-client";

/**
 * Hook to fetch and cache a user's display name by their UID
 * @param uid - The Firebase UID of the user
 * @returns The user's display name or "Unknown User" if not found
 */
export function useUserDisplayName(uid: string | null | undefined): string | null {
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!uid) {
      setDisplayName(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const fetchDisplayName = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", uid));
        
        if (!cancelled) {
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setDisplayName(userData?.displayName || "Unknown User");
          } else {
            setDisplayName("Unknown User");
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching user display name:", error);
        if (!cancelled) {
          setDisplayName("Unknown User");
          setIsLoading(false);
        }
      }
    };

    fetchDisplayName();

    return () => {
      cancelled = true;
    };
  }, [uid]);

  return isLoading ? null : displayName;
}
